<template>
  <div
    class="import-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="import-title"
    @click.self="$emit('close')"
  >
    <div class="import-modal">
      <header class="import-header">
        <h2 id="import-title" class="import-title">Import questionnaire from JSON</h2>
        <button type="button" class="close-btn" aria-label="Close" @click="$emit('close')">×</button>
      </header>

      <div class="import-body">
        <!-- Warning -->
        <div class="warn">
          <p class="warn-title">⚠️ Restricted experimental feature</p>
          <p class="warn-text">
            This imports a questionnaire from a raw JSON payload, bypassing the
            normal editor. It is for development reloads only. It <strong>creates</strong>
            a new questionnaire; it will <strong>refuse and report</strong> if one with this
            name already exists rather than overwrite it. The surrounding reload
            workflow involves <strong>manually deleting questionnaires and questions
            first</strong> — that step is irreversible and can cause
            <strong>massive data loss</strong>. There is no response-safety guard yet.
            Do not use this on data you care about.
          </p>
        </div>

        <!-- File + name -->
        <div class="field">
          <label class="field-label" for="import-file">JSON payload</label>
          <input
            id="import-file"
            ref="fileInput"
            type="file"
            accept=".json,application/json"
            @change="onFileChange"
          >
          <p v-if="parseError" class="field-error">{{ parseError }}</p>
          <p v-else-if="payload" class="field-hint">
            Loaded: {{ payload.sections ? payload.sections.length : 0 }} section(s).
          </p>
        </div>

        <div class="field">
          <label class="field-label" for="import-name">Questionnaire name</label>
          <input
            id="import-name"
            v-model="name"
            type="text"
            placeholder="Name for the new questionnaire"
          >
          <p class="field-hint">
            Authoritative — this is the name used. Slug:
            <code class="mono">{{ name ? derivedSlug : '—' }}</code>
          </p>
        </div>

        <label class="confirm">
          <input type="checkbox" v-model="confirmed">
          <span>I understand this is experimental and may affect data.</span>
        </label>

        <!-- Collision: blocked, offer delete + retry -->
        <div v-if="collision" class="report report-blocked">
          <p class="report-title">Blocked — a questionnaire already exists</p>
          <p class="report-text">
            <strong>{{ collision.name }}</strong>
            (<code class="mono">{{ collision.slug }}</code>)
            already exists<span v-if="collision.is_locked"> and is locked</span>.
            Delete it first, then retry — or change the name above.
          </p>
          <div class="report-actions">
            <button
              type="button"
              class="btn btn-danger"
              :disabled="busy || collision.is_locked"
              @click="deleteAndRetry"
            >{{ busy ? 'Working…' : 'Delete this questionnaire & retry' }}</button>
          </div>
        </div>

        <!-- Error / validation report -->
        <div v-if="report" class="report report-error">
          <p class="report-title">{{ reportTitle }}</p>
          <pre class="report-pre">{{ report }}</pre>
          <button type="button" class="btn btn-secondary btn-sm" @click="copyReport">
            {{ copyLabel }}
          </button>
        </div>
      </div>

      <footer class="import-footer">
        <button type="button" class="btn btn-secondary" :disabled="busy" @click="$emit('close')">Cancel</button>
        <button type="button" class="btn btn-primary" :disabled="!canImport" @click="runImport">
          {{ busy ? 'Importing…' : 'Import' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import {
  validateImportPayload,
  findCollision,
  importQuestionnaire,
  slugify,
} from './_data/questionnaireImport.js';

export default {
  name: 'ImportQuestionnaireModal',
  emits: ['close', 'imported'],
  data() {
    return {
      payload: null,
      parseError: null,
      name: '',
      confirmed: false,
      busy: false,
      report: '',
      reportTitle: '',
      collision: null,
      copyLabel: 'Copy report',
    };
  },
  computed: {
    derivedSlug() {
      return slugify(this.name);
    },
    canImport() {
      return !!this.payload && !!this.name.trim() && this.confirmed && !this.busy;
    },
  },
  methods: {
    onFileChange(e) {
      this.parseError = null;
      this.payload = null;
      this.report = '';
      this.collision = null;
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result);
          this.payload = parsed;
          // Prefill the name from the payload if it carries a plain string.
          const pname = parsed && parsed.export && parsed.export.name;
          if (!this.name && typeof pname === 'string') this.name = pname;
        } catch (err) {
          this.parseError = `Could not parse JSON: ${err.message}`;
        }
      };
      reader.onerror = () => { this.parseError = 'Could not read the file.'; };
      reader.readAsText(file);
    },

    async runImport() {
      if (!this.canImport) return;
      this.report = '';
      this.collision = null;

      // 1. Structural validation.
      const errors = validateImportPayload(this.payload);
      if (errors.length) {
        this.reportTitle = 'Payload did not validate';
        this.report = this._validationReport(errors);
        return;
      }

      this.busy = true;
      try {
        // 2. Collision guard.
        const { existing } = await findCollision(this.name);
        if (existing) {
          this.collision = existing;
          return;
        }
        // 3. Create.
        const result = await importQuestionnaire(this.payload, this.name);
        if (result.ok) {
          this.$emit('imported', { slug: result.slug, counts: result.counts });
          return;
        }
        this.reportTitle = 'Import failed';
        this.report = this._failureReport(result);
      } catch (e) {
        this.reportTitle = 'Import failed';
        this.report = `Unexpected error: ${this._errText(e)}`;
      } finally {
        this.busy = false;
      }
    },

    async deleteAndRetry() {
      if (!this.collision || this.busy) return;
      this.busy = true;
      try {
        await apiService.deleteQuestionnaire(this.collision.id);
        this.collision = null;
      } catch (e) {
        this.reportTitle = 'Could not delete the existing questionnaire';
        this.report = this._errText(e);
        this.busy = false;
        return;
      }
      this.busy = false;
      await this.runImport();
    },

    copyReport() {
      const text = this.report;
      const done = () => {
        this.copyLabel = 'Copied!';
        setTimeout(() => { this.copyLabel = 'Copy report'; }, 1200);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {});
      }
    },

    _errText(e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
      if (typeof detail === 'string') return detail;
      return (e && e.message) || 'Unknown error.';
    },

    _validationReport(errors) {
      return [
        'Questionnaire JSON import was rejected before any data was created.',
        `Name: ${this.name}`,
        '',
        'Problems:',
        ...errors.map(x => `  - ${x}`),
        '',
        'Fix the payload and regenerate, then re-import.',
      ].join('\n');
    },

    _failureReport(result) {
      const lines = [
        'Questionnaire JSON import failed partway through.',
        `Name: ${this.name}  Slug: ${result.slug}`,
        `Failed at: ${result.step || 'unknown step'}`,
        `HTTP status: ${result.status != null ? result.status : 'n/a'}`,
        result.rolledBack
          ? 'The partially-created questionnaire was deleted (rolled back).'
          : 'WARNING: rollback FAILED — a partial questionnaire may remain; delete it by hand.',
        '',
        `Error: ${this._errText(result.error)}`,
      ];
      if (result.lastPayload) {
        lines.push('', 'Payload sent for the failing step:', JSON.stringify(result.lastPayload, null, 2));
      }
      if (result.status === 500) {
        lines.push(
          '',
          'A 500 is an unhandled server error — its real cause is only in the',
          'backend logs (look for "Failed to create section/question ... : <error>").',
        );
      }
      lines.push('', 'Fix the payload (or the catalogue) and re-import.');
      return lines.join('\n');
    },
  },
};
</script>

<style scoped>
.import-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4vh 2vw;
  z-index: 2600;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.import-modal {
  background: #fff;
  border-radius: 12px;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  width: 90vw;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.import-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.12);
}

.import-title { font-size: 16px; font-weight: 600; margin: 0; color: #2c2c2a; }

.close-btn {
  background: transparent; border: none; font-size: 22px; line-height: 1;
  color: #5f5e5a; cursor: pointer; padding: 0 6px;
}

.import-body { padding: 16px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; }

.warn {
  background: #FBEAE7; border: 0.5px solid #E0B4AC; border-radius: 8px; padding: 12px 14px;
}
.warn-title { margin: 0 0 6px; font-size: 13px; font-weight: 700; color: #A92F26; }
.warn-text { margin: 0; font-size: 12px; line-height: 1.5; color: #6b3a34; }

.field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 13px; font-weight: 600; color: #2c2c2a; }
.field input[type="text"] {
  padding: 10px 12px; border: 0.5px solid rgba(0, 0, 0, 0.2); border-radius: 8px;
  font-size: 14px; font-family: inherit;
}
.field-hint { margin: 0; font-size: 12px; color: #888780; }
.field-error { margin: 0; font-size: 12px; color: #A92F26; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }

.confirm { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: #2c2c2a; cursor: pointer; }
.confirm input { margin-top: 2px; width: 16px; height: 16px; accent-color: #534AB7; }

.report { border-radius: 8px; padding: 12px 14px; }
.report-blocked { background: #FFF6E6; border: 0.5px solid #E8C98A; }
.report-error { background: #f6f6f3; border: 0.5px solid rgba(0, 0, 0, 0.12); }
.report-title { margin: 0 0 8px; font-size: 13px; font-weight: 600; color: #2c2c2a; }
.report-text { margin: 0 0 10px; font-size: 13px; line-height: 1.5; color: #5f5e5a; }
.report-actions { display: flex; gap: 10px; }
.report-pre {
  margin: 0 0 10px; padding: 10px 12px; background: rgba(0, 0, 0, 0.04);
  border-radius: 6px; font-size: 12px; line-height: 1.45; white-space: pre-wrap;
  word-break: break-word; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  max-height: 220px; overflow: auto;
}

.import-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 20px; border-top: 0.5px solid rgba(0, 0, 0, 0.12);
}

.btn {
  padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 500;
  font-family: inherit; cursor: pointer; border: 0.5px solid rgba(0, 0, 0, 0.15);
}
.btn:disabled { cursor: not-allowed; opacity: 0.45; }
.btn-sm { padding: 6px 12px; font-size: 12px; }
.btn-primary { background: #534AB7; color: #fff; border-color: #534AB7; }
.btn-primary:hover:not(:disabled) { background: #3F368F; }
.btn-secondary { background: #fff; color: #2c2c2a; }
.btn-secondary:hover:not(:disabled) { background: #f5f4ef; }
.btn-danger { background: #C8392E; color: #fff; border-color: #C8392E; }
.btn-danger:hover:not(:disabled) { background: #A92F26; }
</style>
